'use client';

import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { FC } from 'react';
import { LogoProps } from '../Logo';
import { CardCarousel } from './CardCarousel';
import { CarouselNavigation } from './CarouselNavigation';

export type UseCaseCustomerCarouselProps = {
  title: string;
  masterMarginsTop?: boolean;
  masterMarginsBottom?: boolean;

  customers: {
    icon: React.ReactNode | LogoProps['type'];
    quote: React.ReactNode;
    authorName: string;
    authorTitle: string;
  }[];
}

/**
 * The UseCaseCustomerCarousel component is a carousel that displays customer quotes.
 * In Craft, it's called "Customer Quotes".
 */
const UseCaseCustomerCarousel: FC<UseCaseCustomerCarouselProps> = ({ title, customers = [], masterMarginsTop, masterMarginsBottom }) => {
  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    drag: true,
    slides: {
      origin: 'center',
      perView: 1,
      spacing: 32,
    },
    breakpoints: {
      '(min-width: 600px)': {
        slides: {
          origin: 'center',
          perView: 2,
          spacing: 32,
        },
      },
      '(min-width: 1000px)': {
        slides: {
          origin: 'center',
          perView: 2.75,
          spacing: 32,
        },
      },
      '(min-width: 1600px)': {
        slides: {
          origin: 'center',
          perView: 4,
          spacing: 32,
        },
      },
    },
  });

  const onClickPrev = () => {
    if (!slider.current) return;
    slider.current.prev();
  };

  const onClickNext = () => {
    if (!slider.current) return;
    slider.current.next();
  };

  return (
    <div className={`${masterMarginsTop ? `mt-[150px] ` : "mt-8 md:mt-22"} ${masterMarginsBottom ? `mb-[150px] ` : "mb-8 md:mb-22"} overflow-hidden flex flex-col gap-y-6 md:gap-y-8`}>
      <div className="px-container-mobile md:px-container-desktop">
        <div className="max-w-grid mx-auto">
          <div className="grid md:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop md:text-center">
            <div className="md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4 flex flex-col gap-3">
              <h2 className="font-500 tracking-tight text-30/36 md:text-52/62">{title}</h2>
            </div>
          </div>
        </div>
      </div>
      <div className="relative flex flex-col gap-3">
        <CardCarousel customers={customers} sliderRef={sliderRef} />
        <div className="hidden md:block absolute top-0 bottom-0 left-0 h-full w-30 bg-gradient-to-r from-white via-white to-transparent pointer-events-none" />
        <div className="hidden md:block absolute top-0 bottom-0 right-0 h-full w-30 bg-gradient-to-l from-white via-white to-transparent pointer-events-none" />
        <CarouselNavigation onClickPrev={onClickPrev} onClickNext={onClickNext} />
      </div>
    </div>
  );
};

export default UseCaseCustomerCarousel;
